import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixFunctionsV1Function from './functions-v1-function-functions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** A function is the main entity in the Wix Functions ecosystem. All other entities are used to create functions or to define their behavior. Learn more about [Functions](https://support.wix.com/en/wix-functions). */
export interface _Function {
  /**
   * Function ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the function is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the function.
   *
   * Ignored when creating a function.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the function was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the function was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the function type that defines when and how the function runs. This is the function type that the function is based on.
   * @format GUID
   */
  functionExtensionId?: string | null;
  /**
   * Display name for the function in the dashboard's function builder.
   * @maxLength 100
   */
  functionName?: string | null;
  /**
   * Activation status.
   * @readonly
   */
  activationStatus?: ActivationStatusWithLiterals;
  /**
   * ID of the Wix app that defines the function type that the function is based on.
   * @format GUID
   */
  appId?: string | null;
  /**
   * Display name of the function type.
   * @minLength 1
   * @maxLength 100
   */
  functionExtensionName?: string | null;
  /**
   * External IDs that link the function to other entities.
   * @internal
   * @format GUID
   * @maxSize 100
   * @readonly
   */
  externalIds?: string[] | null;
  /**
   * ID of the function template used to create this function.
   * @format GUID
   */
  functionTemplateExtensionId?: string | null;
  /**
   * ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.
   * @format GUID
   */
  formTemplateExtensionId?: string | null;
  /** Whether the function has changes that were made since it was last activated. */
  hasUnpublishedChanges?: boolean | null;
  /** Tags assigned to the function for organization and filtering. */
  tags?: Tags;
}

export enum ActivationStatus {
  /** Function is active and runs when triggered. */
  ACTIVE = 'ACTIVE',
  /** Function is inactive and doesn't run when triggered. */
  INACTIVE = 'INACTIVE',
  /** Function is saved but has never been activated. */
  DRAFT = 'DRAFT',
}

/** @enumType */
export type ActivationStatusWithLiterals =
  | ActivationStatus
  | 'ACTIVE'
  | 'INACTIVE'
  | 'DRAFT';

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * option (.wix.api.decomposite_of) = "wix.commons.v2.tags.Foo";
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 *
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * public_tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, typically restricted from site members and visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone with access to the entity, including site members and visitors. */
  publicTags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface DraftDiscarded {
  /**
   * Function ID associated with the discarded draft
   * @format GUID
   */
  functionId?: string;
  /** Date and time when the draft was discarded */
  discardedDate?: Date | null;
}

export interface TagsModified {
  /** Updated function. */
  function?: _Function;
  /** Tags that were assigned to the Function. */
  assignedTags?: Tags;
  /** Tags that were unassigned from the Function. */
  unassignedTags?: Tags;
}

export interface CreateFunctionRequest {
  /** Function to create. */
  function: _Function;
}

export interface CreateFunctionResponse {
  /** The created function. */
  function?: _Function;
}

export interface GetFunctionRequest {
  /**
   * ID of the function to retrieve.
   * @format GUID
   */
  functionId: string;
}

export interface GetFunctionResponse {
  /** The retrieved function. */
  function?: _Function;
}

export interface UpdateFunctionRequest {
  /** Function to update. */
  function: _Function;
  /**
   * Set of fields to update.
   *
   * Fields that aren't included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateFunctionResponse {
  /** Updated function. */
  function?: _Function;
}

export interface DeleteFunctionRequest {
  /**
   * ID of the function to delete.
   * @format GUID
   */
  functionId: string;
}

export interface DeleteFunctionResponse {}

export interface QueryFunctionsRequest {
  /** Wix Query Language expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryFunctionsResponse {
  /** List of retrieved functions. */
  functions?: _Function[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface ListValuesByFieldRequest {
  /**
   * Field to list values for.
   * @minLength 1
   * @maxLength 100
   */
  field?: string;
}

export interface ListValuesByFieldResponse {
  /**
   * List of values for the field.
   * @maxSize 100
   */
  fieldAndCount?: FieldAndCount[];
}

export interface FieldAndCount {
  /**
   * Field value.
   * @minLength 1
   * @maxLength 100
   */
  field?: string;
  /** Number of times the value occurs. */
  count?: number;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface UpdateFunctionExternalIdsRequest {
  /**
   * ID of the function to update.
   * @format GUID
   */
  functionId?: string;
  /**
   * External ids to set.
   * @format GUID
   * @maxSize 100
   */
  externalIds?: string[];
}

export interface UpdateFunctionExternalIdsResponse {
  /** The updated function. */
  function?: _Function;
}

export interface BulkUpdateFunctionTagsRequest {
  /**
   * IDs of functions to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
  /** List of tags to assign. */
  assignTags: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateFunctionTagsResponse {
  /**
   * Results.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateFunctionTagsResult[];
  /** Bulk action metadata. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Provided only whenever possible. For example, `itemId` can't be provided when item creation has failed.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action for this item was successful. When `false`, the `error` field is returned. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateFunctionTagsResult {
  /** Item metadata. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateFunctionTagsByFilterRequest {
  /** Filter. */
  filter: Record<string, any> | null;
  /** List of tags to assign. */
  assignTags: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}

export interface BulkUpdateFunctionTagsByFilterResponse {
  /**
   * Job ID for the bulk update operation.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateFunctionTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateFunctionTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface FunctionCreatedEnvelope {
  entity: _Function;
  metadata: EventMetadata;
}

/**
 * Triggered when a function is created.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope View Forms
 * @permissionScopeId SCOPE.FORMS.VIEW-FORM
 * @permissionScope Manage form submissions.
 * @permissionScopeId SCOPE.FORMS.MANAGE-SUBMISSIONS
 * @permissionScope Manage forms
 * @permissionScopeId SCOPE.FORMS.EDIT-FORM
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Functions
 * @permissionScopeId SCOPE.DC-FUNCTIONS.MANAGE-FUNCTIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @webhook
 * @eventType wix.functions.v1.function_created
 * @slug created
 */
export declare function onFunctionCreated(
  handler: (event: FunctionCreatedEnvelope) => void | Promise<void>
): void;

export interface FunctionDeletedEnvelope {
  entity: _Function;
  metadata: EventMetadata;
}

/**
 * Triggered when a function is deleted.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope View Forms
 * @permissionScopeId SCOPE.FORMS.VIEW-FORM
 * @permissionScope Manage form submissions.
 * @permissionScopeId SCOPE.FORMS.MANAGE-SUBMISSIONS
 * @permissionScope Manage forms
 * @permissionScopeId SCOPE.FORMS.EDIT-FORM
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Functions
 * @permissionScopeId SCOPE.DC-FUNCTIONS.MANAGE-FUNCTIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @webhook
 * @eventType wix.functions.v1.function_deleted
 * @slug deleted
 */
export declare function onFunctionDeleted(
  handler: (event: FunctionDeletedEnvelope) => void | Promise<void>
): void;

export interface FunctionTagsModifiedEnvelope {
  data: TagsModified;
  metadata: EventMetadata;
}

/**
 * Triggered when tags are modified.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope View Forms
 * @permissionScopeId SCOPE.FORMS.VIEW-FORM
 * @permissionScope Manage form submissions.
 * @permissionScopeId SCOPE.FORMS.MANAGE-SUBMISSIONS
 * @permissionScope Manage forms
 * @permissionScopeId SCOPE.FORMS.EDIT-FORM
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Functions
 * @permissionScopeId SCOPE.DC-FUNCTIONS.MANAGE-FUNCTIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @webhook
 * @eventType wix.functions.v1.function_tags_modified
 * @slug tags_modified
 */
export declare function onFunctionTagsModified(
  handler: (event: FunctionTagsModifiedEnvelope) => void | Promise<void>
): void;

export interface FunctionUpdatedEnvelope {
  entity: _Function;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a function is updated.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope View Forms
 * @permissionScopeId SCOPE.FORMS.VIEW-FORM
 * @permissionScope Manage form submissions.
 * @permissionScopeId SCOPE.FORMS.MANAGE-SUBMISSIONS
 * @permissionScope Manage forms
 * @permissionScopeId SCOPE.FORMS.EDIT-FORM
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Functions
 * @permissionScopeId SCOPE.DC-FUNCTIONS.MANAGE-FUNCTIONS
 * @permissionScope Manage Restaurants - all permissions
 * @permissionScopeId SCOPE.RESTAURANTS.MEGA-SCOPES
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @webhook
 * @eventType wix.functions.v1.function_updated
 * @slug updated
 */
export declare function onFunctionUpdated(
  handler: (event: FunctionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a function with the specified configuration. After creation, you'll need to attach automation logic using the Function Methods API and configure any required service plugin settings before the function can be activated.
 *
 * This isn't the recommended way to create a function. Learn more about [function creation](https://dev.wix.com/docs/api-reference/business-management/functions/about-function-creation-and-activation#quick-creation-and-configuration).
 * @param _function - Function to create.
 * @public
 * @requiredField function
 * @requiredField function.appId
 * @requiredField function.functionExtensionId
 * @permissionId FUNCTIONS.FUNCTION_CREATE
 * @applicableIdentity APP
 * @returns The created function.
 * @fqn wix.functions.api.v1.Functions.CreateFunction
 */
export async function createFunction(
  _function?: _Function
): Promise<
  NonNullablePaths<_Function, `activationStatus` | `tags.privateTags.tagIds`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    function: _function,
  });

  const reqOpts = ambassadorWixFunctionsV1Function.createFunction(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.function!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { function: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_function']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a function.
 * @param functionId - ID of the function to retrieve.
 * @public
 * @requiredField functionId
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @applicableIdentity APP
 * @returns The retrieved function.
 * @fqn wix.functions.api.v1.Functions.GetFunction
 */
export async function getFunction(
  functionId: string
): Promise<
  NonNullablePaths<_Function, `activationStatus` | `tags.privateTags.tagIds`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    functionId: functionId,
  });

  const reqOpts = ambassadorWixFunctionsV1Function.getFunction(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.function!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { functionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['functionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a function.
 *
 * Each time the function is updated,
 * `revision` increments by 1.
 * The existing `revision` must be passed when updating the function.
 * This ensures you're working with the latest version of the function
 * and prevents unintended overwrites.
 * @param _id - Function ID.
 * @public
 * @requiredField _id
 * @requiredField function
 * @requiredField function.revision
 * @permissionId FUNCTIONS.FUNCTION_UPDATE
 * @applicableIdentity APP
 * @returns Updated function.
 * @fqn wix.functions.api.v1.Functions.UpdateFunction
 */
export async function updateFunction(
  _id: string,
  _function?: UpdateFunction
): Promise<
  NonNullablePaths<_Function, `activationStatus` | `tags.privateTags.tagIds`, 4>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    function: { ..._function, id: _id },
  });

  const reqOpts = ambassadorWixFunctionsV1Function.updateFunction(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.function!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { function: '$[1]' },
        explicitPathsToArguments: { 'function.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', '_function']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateFunction {
  /**
   * Function ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the function is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the function.
   *
   * Ignored when creating a function.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the function was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the function was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * ID of the function type that defines when and how the function runs. This is the function type that the function is based on.
   * @format GUID
   */
  functionExtensionId?: string | null;
  /**
   * Display name for the function in the dashboard's function builder.
   * @maxLength 100
   */
  functionName?: string | null;
  /**
   * Activation status.
   * @readonly
   */
  activationStatus?: ActivationStatusWithLiterals;
  /**
   * ID of the Wix app that defines the function type that the function is based on.
   * @format GUID
   */
  appId?: string | null;
  /**
   * Display name of the function type.
   * @minLength 1
   * @maxLength 100
   */
  functionExtensionName?: string | null;
  /**
   * External IDs that link the function to other entities.
   * @internal
   * @format GUID
   * @maxSize 100
   * @readonly
   */
  externalIds?: string[] | null;
  /**
   * ID of the function template used to create this function.
   * @format GUID
   */
  functionTemplateExtensionId?: string | null;
  /**
   * ID of the form template used to create this function. Included if the function was created using the [Builderless Productions](https://dev.wix.com/docs/api-reference/business-management/functions/builderless-productions/introduction) API.
   * @format GUID
   */
  formTemplateExtensionId?: string | null;
  /** Whether the function has changes that were made since it was last activated. */
  hasUnpublishedChanges?: boolean | null;
  /** Tags assigned to the function for organization and filtering. */
  tags?: Tags;
}

/**
 * Deletes a function.
 *
 * Deleting a function permanently removes it from the Function List
 * in the site's dashboard.
 * @param functionId - ID of the function to delete.
 * @public
 * @requiredField functionId
 * @permissionId FUNCTIONS.FUNCTION_DELETE
 * @applicableIdentity APP
 * @fqn wix.functions.api.v1.Functions.DeleteFunction
 */
export async function deleteFunction(functionId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    functionId: functionId,
  });

  const reqOpts = ambassadorWixFunctionsV1Function.deleteFunction(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { functionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['functionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of functions.
 *
 * The Query Functions method builds a query to retrieve a list of functions and returns a `FunctionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `FunctionsQueryBuilder` methods onto the query. `FunctionsQueryBuilder` methods enable you to filter, sort, and control the results that Query Functions returns.
 *
 * Query Functions has a default paging limit of 50, which you can override.
 *
 *  For a full description of the item object, see the object returned for the `items` property in `FunctionsQueryResult`.
 * @public
 * @permissionId FUNCTIONS.FUNCTION_READ
 * @applicableIdentity APP
 * @fqn wix.functions.api.v1.Functions.QueryFunctions
 */
export function queryFunctions(): FunctionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    _Function,
    'CURSOR',
    QueryFunctionsRequest,
    QueryFunctionsResponse
  >({
    func: async (payload: QueryFunctionsRequest) => {
      const reqOpts = ambassadorWixFunctionsV1Function.queryFunctions(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryFunctionsRequest['query']) => {
      const args = [query, {}] as [QueryFunctionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryFunctionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.functions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface FunctionsQueryResult extends QueryCursorResult {
  items: _Function[];
  query: FunctionsQueryBuilder;
  next: () => Promise<FunctionsQueryResult>;
  prev: () => Promise<FunctionsQueryResult>;
}

export interface FunctionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'functionExtensionId'
      | 'functionName'
      | 'appId'
      | 'functionExtensionName',
    value: string
  ) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: string, value: any[]) => FunctionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (propertyName: string, value: any[]) => FunctionsQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName',
    value: any
  ) => FunctionsQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName',
    value: boolean
  ) => FunctionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName'
    >
  ) => FunctionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | '_createdDate'
      | '_updatedDate'
      | 'functionExtensionId'
      | 'functionName'
      | 'activationStatus'
      | 'appId'
      | 'functionExtensionName'
    >
  ) => FunctionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => FunctionsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => FunctionsQueryBuilder;
  find: () => Promise<FunctionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.functions.api.v1.Functions.QueryFunctions
 * @requiredField query
 */
export async function typedQueryFunctions(
  query: _FunctionQuery
): Promise<
  NonNullablePaths<
    QueryFunctionsResponse,
    `functions` | `functions.${number}.activationStatus`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixFunctionsV1Function.queryFunctions(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface _FunctionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_id',
        '_updatedDate',
        'activationStatus',
        'appId',
        'functionExtensionId',
        'functionExtensionName',
        'functionName'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  _Function,
  _FunctionQuerySpec
>;
export type _FunctionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    _Function,
    _FunctionQuerySpec,
    _FunctionQuery
  >(),
};

/**
 * Synchronously update tags on multiple functions.
 * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 * @param ids - IDs of functions to update tags for.
 * @public
 * @requiredField ids
 * @requiredField options.assignTags
 * @permissionId FUNCTIONS.FUNCTION_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.functions.api.v1.Functions.BulkUpdateFunctionTags
 */
export async function bulkUpdateFunctionTags(
  ids: string[],
  options?: NonNullablePaths<BulkUpdateFunctionTagsOptions, `assignTags`, 2>
): Promise<
  NonNullablePaths<
    BulkUpdateFunctionTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateFunctionTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ids: ids,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixFunctionsV1Function.bulkUpdateFunctionTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ids: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['ids', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateFunctionTagsOptions {
  /** List of tags to assign. */
  assignTags: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}

/**
 * Asynchronously update tags on multiple functions.
 * An empty filter updates all functions.
 * A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 * @param filter - Filter.
 * @public
 * @requiredField filter
 * @requiredField options.assignTags
 * @permissionId FUNCTIONS.FUNCTION_UPDATE_TAGS
 * @applicableIdentity APP
 * @fqn wix.functions.api.v1.Functions.BulkUpdateFunctionTagsByFilter
 */
export async function bulkUpdateFunctionTagsByFilter(
  filter: Record<string, any>,
  options?: NonNullablePaths<
    BulkUpdateFunctionTagsByFilterOptions,
    `assignTags`,
    2
  >
): Promise<
  NonNullablePaths<BulkUpdateFunctionTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateFunctionTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixFunctionsV1Function.bulkUpdateFunctionTagsByFilter(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateFunctionTagsByFilterOptions {
  /** List of tags to assign. */
  assignTags: Tags;
  /** List of tags to unassign. */
  unassignTags?: Tags;
}
